<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

const auth = useAuthStore();
const route = useRoute();

const showAuthNav = computed(() => auth.status !== 'idle' && auth.status !== 'loading');
</script>

<template>
  <header class="header">
    <div class="header-inner">
      <RouterLink :to="{ name: 'home' }" class="brand">
        <span class="brand-glyph" aria-hidden="true">◆</span>
        <span class="brand-name">watch<span class="brand-dot">.</span>silxnt</span>
      </RouterLink>

      <nav class="nav" v-if="showAuthNav">
        <template v-if="auth.isAuthenticated">
          <RouterLink :to="{ name: 'search' }" class="nav-link" active-class="nav-link--active">
            Suchen
          </RouterLink>
          <RouterLink :to="{ name: 'list' }" class="nav-link" active-class="nav-link--active">
            Meine Liste
          </RouterLink>
          <RouterLink
            v-if="auth.user"
            :to="{ name: 'profile', params: { discord_id: auth.user.id } }"
            class="nav-link"
            :class="{ 'nav-link--active': route.name === 'profile' && route.params.discord_id === auth.user.id }"
          >
            Profil
          </RouterLink>
        </template>
      </nav>

      <div class="header-actions" v-if="showAuthNav">
        <template v-if="auth.isAuthenticated && auth.user">
          <div class="user-chip" :title="auth.user.username">
            <img :src="auth.user.avatar" :alt="auth.user.username" class="user-avatar" />
            <span class="user-name">{{ auth.user.username }}</span>
          </div>
          <button class="btn btn--ghost btn--sm" @click="auth.logout()">
            Abmelden
          </button>
        </template>
        <button v-else class="btn btn--primary btn--sm" @click="auth.loginWithDiscord()">
          Mit Discord anmelden
        </button>
      </div>
    </div>
  </header>
</template>

<style scoped>
.header {
  position: sticky;
  top: 0;
  z-index: 20;
  background: rgba(10, 10, 18, 0.72);
  backdrop-filter: blur(14px) saturate(140%);
  -webkit-backdrop-filter: blur(14px) saturate(140%);
  border-bottom: 1px solid var(--color-border-subtle);
}

.header-inner {
  width: min(1200px, 100% - 2rem);
  margin: 0 auto;
  padding: 0.85rem 0;
  display: flex;
  align-items: center;
  gap: 1.25rem;
}

.brand {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 1.05rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-text-primary);
}

.brand-glyph {
  color: var(--color-accent-primary);
  font-size: 0.95rem;
  transform: translateY(-1px);
  text-shadow: 0 0 12px var(--color-accent-primary);
}

.brand-dot {
  color: var(--color-accent-secondary);
}

.nav {
  display: flex;
  gap: 0.25rem;
  margin-left: 1rem;
}

.nav-link {
  position: relative;
  padding: 0.45rem 0.85rem;
  border-radius: var(--radius-md);
  font-family: var(--font-display);
  font-size: 0.85rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--color-text-muted);
  transition: color 0.18s ease, background 0.18s ease;
}

.nav-link:hover {
  color: var(--color-text-primary);
  background: rgba(255, 255, 255, 0.03);
}

.nav-link--active {
  color: var(--color-text-primary);
  background: rgba(192, 132, 252, 0.08);
}

.nav-link--active::after {
  content: '';
  position: absolute;
  left: 0.85rem;
  right: 0.85rem;
  bottom: 0.15rem;
  height: 2px;
  background: linear-gradient(90deg, var(--color-accent-primary), var(--color-accent-secondary));
  border-radius: 999px;
}

.header-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.user-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.3rem 0.7rem 0.3rem 0.3rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-pill);
}

.user-avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  object-fit: cover;
}

.user-name {
  font-size: 0.85rem;
  color: var(--color-text-secondary);
  max-width: 8rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.btn--sm {
  padding: 0.45rem 0.85rem;
  font-size: 0.78rem;
}

@media (max-width: 760px) {
  .nav {
    display: none;
  }
  .user-name {
    display: none;
  }
}
</style>
